import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Sparkles, Target, Zap, Heart, TrendingUp, Brain, CircleDot, ArrowUp, Clock, PieChart, Shield, Sun, Compass, RefreshCw, Flame, GitBranch, Minus, DollarSign, BookOpen, Filter, CheckCircle, Award, Users } from 'lucide-react';
import { philosophiesCroissance, statsPhilosophie, type Philosophie } from '../data/philosophiesCroissance';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface PhilosophieCroissanceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PhilosophieCroissanceModal: React.FC<PhilosophieCroissanceModalProps> = ({ isOpen, onClose }) => {
  const [ongletActif, setOngletActif] = useState<'intro' | 'concepts'>('intro');
  const [categorieActive, setCategorieActive] = useState<'Toutes' | Philosophie['categorie']>('Toutes');
  const [philosophieSelectionnee, setPhilosophieSelectionnee] = useState<Philosophie | null>(null);
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['philosophie_croissance_last_ai_analysis'] ?? null) : null;

  if (!isOpen) return null;

  const iconeMap: { [key: string]: React.ReactNode } = {
    'TrendingUp': <TrendingUp className="w-6 h-6" />,
    'Zap': <Zap className="w-6 h-6" />,
    'Target': <Target className="w-6 h-6" />,
    'Sparkles': <Sparkles className="w-6 h-6" />,
    'Heart': <Heart className="w-6 h-6" />,
    'CircleDot': <CircleDot className="w-6 h-6" />,
    'ArrowUp': <ArrowUp className="w-6 h-6" />,
    'Clock': <Clock className="w-6 h-6" />,
    'PieChart': <PieChart className="w-6 h-6" />,
    'Shield': <Shield className="w-6 h-6" />,
    'Sun': <Sun className="w-6 h-6" />,
    'Compass': <Compass className="w-6 h-6" />,
    'RefreshCw': <RefreshCw className="w-6 h-6" />,
    'Flame': <Flame className="w-6 h-6" />,
    'Brain': <Brain className="w-6 h-6" />,
    'GitBranch': <GitBranch className="w-6 h-6" />,
    'Minus': <Minus className="w-6 h-6" />,
    'DollarSign': <DollarSign className="w-6 h-6" />,
    'Users': <Users className="w-6 h-6" />,
    'BookOpen': <BookOpen className="w-6 h-6" />
  };

  const couleurCategorie = {
    'Mindset': 'bg-blue-100 text-blue-700 border-blue-300',
    'Action': 'bg-orange-100 text-orange-700 border-orange-300',
    'Bonheur': 'bg-yellow-100 text-yellow-700 border-yellow-300',
    'Humilité': 'bg-purple-100 text-purple-700 border-purple-300',
    'Croissance': 'bg-green-100 text-green-700 border-green-300'
  };

  const philosophiesFiltrees = categorieActive === 'Toutes' 
    ? philosophiesCroissance 
    : philosophiesCroissance.filter(p => p.categorie === categorieActive);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        
        {/* HEADER */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white p-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-white/10"></div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors z-10 cursor-pointer"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="relative z-10">
            <div className="flex items-center gap-4 mb-3">
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-3">
                <Brain className="w-10 h-10" />
              </div>
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-4xl font-black">Philosophie de Croissance BZ</h2>
                  {/* Badge AI - Disponible dans AI Assistant */}
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                    className="flex items-center gap-1.5 px-3 py-1.5 backdrop-blur-sm bg-purple-500/40 border border-purple-400/60 rounded-full shadow-lg shadow-purple-500/20"
                    title="Disponible dans AI Assistant"
                  >
                    <Brain className="w-4 h-4 text-purple-200" />
                    <span className="text-xs text-purple-100 font-semibold">AI</span>
                  </motion.div>
                </div>
                <p className="text-indigo-100 text-lg mb-2">
                  22 concepts pour devenir la meilleure version de toi-même
                </p>
                {lastAnalysisDate && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-purple-200">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/20">
                <div className="text-2xl font-bold">{statsPhilosophie.totalConcepts}</div>
                <div className="text-indigo-100 text-xs">Concepts</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/20">
                <div className="text-2xl font-bold">5</div>
                <div className="text-indigo-100 text-xs">Catégories</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 border border-white/20">
                <div className="text-2xl font-bold">∞</div>
                <div className="text-indigo-100 text-xs">Impact</div>
              </div>
            </div>
          </div>
        </div>

        {/* NAVIGATION TABS */}
        <div className="border-b border-gray-200 bg-gray-50 px-8">
          <div className="flex gap-2">
            <button
              onClick={() => setOngletActif('intro')}
              className={`px-6 py-3 font-semibold transition-all ${
                ongletActif === 'intro'
                  ? 'bg-white text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Introduction
              </div>
            </button>
            <button
              onClick={() => setOngletActif('concepts')}
              className={`px-6 py-3 font-semibold transition-all ${
                ongletActif === 'concepts'
                  ? 'bg-white text-purple-600 border-b-2 border-purple-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                Les 22 Concepts
              </div>
            </button>
          </div>
        </div>

        {/* CONTENU */}
        <div className="flex-1 overflow-y-auto p-8">
          
          {/* ONGLET INTRODUCTION */}
          {ongletActif === 'intro' && (
            <div className="space-y-8 max-w-5xl mx-auto">
              
              {/* Message personnel */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl p-8">
                <div className="flex items-start gap-4">
                  <div className="bg-purple-500 rounded-full p-3 flex-shrink-0">
                    <Heart className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">Un message authentique</h3>
                    <p className="text-gray-700 leading-relaxed text-lg mb-4">
                      Ces 22 concepts ne sont pas de la théorie sortie d'un livre MBA. Ce sont des **principes philosophiques appliqués** 
                      que nous vivons quotidiennement chez BZ pour devenir de meilleures versions de nous-mêmes.
                    </p>
                    <p className="text-gray-700 leading-relaxed text-lg mb-4">
                      Certains viennent de livres que j'ai lus et qui m'ont transformé. D'autres sont des concepts psychologiques 
                      que j'essaie d'adapter à mon quotidien et qui **fonctionnent**. Pas parfaitement, pas tout le temps, mais progressivement.
                    </p>
                    <p className="text-gray-700 leading-relaxed text-lg font-medium">
                      Si un seul de ces concepts résonne avec toi et change ta semaine, cette modal aura fait son travail. 🚀
                    </p>
                  </div>
                </div>
              </div>

              {/* Les 5 catégories */}
              <div>
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <Filter className="w-7 h-7 text-indigo-600" />
                  5 Catégories de Croissance
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-white border-2 border-blue-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-blue-500 w-12 h-12 rounded-xl flex items-center justify-center">
                        <Brain className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-gray-900">Mindset</h4>
                        <p className="text-sm text-gray-600">{statsPhilosophie.categories.Mindset} concepts</p>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Comment penser, percevoir les défis, gérer l'adversité et adopter une mentalité de croissance.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-orange-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-orange-500 w-12 h-12 rounded-xl flex items-center justify-center">
                        <Zap className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-gray-900">Action</h4>
                        <p className="text-sm text-gray-600">{statsPhilosophie.categories.Action} concepts</p>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Passer de la réflexion à l'exécution, prendre des décisions, assumer la responsabilité de ses résultats.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-yellow-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-yellow-500 w-12 h-12 rounded-xl flex items-center justify-center">
                        <Sun className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-gray-900">Bonheur</h4>
                        <p className="text-sm text-gray-600">{statsPhilosophie.categories.Bonheur} concepts</p>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Définir ses priorités, aligner ses actions avec ses rêves, cultiver la gratitude et trouver son ikigai.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-purple-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-purple-500 w-12 h-12 rounded-xl flex items-center justify-center">
                        <Heart className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-gray-900">Humilité</h4>
                        <p className="text-sm text-gray-600">{statsPhilosophie.categories.Humilité} concepts</p>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Dompter l'égo, reconnaître son ignorance, apprendre constamment et rester humble face à la complexité.
                    </p>
                  </div>

                  <div className="bg-white border-2 border-green-200 rounded-xl p-6 hover:shadow-lg transition-shadow col-span-2">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-green-500 w-12 h-12 rounded-xl flex items-center justify-center">
                        <TrendingUp className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-gray-900">Croissance</h4>
                        <p className="text-sm text-gray-600">{statsPhilosophie.categories.Croissance} concepts</p>
                      </div>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      Amélioration continue, feedback loops, anti-fragilité, growth mindset et transformation progressive.
                    </p>
                  </div>
                </div>
              </div>

              {/* Comment utiliser */}
              <div className="bg-gradient-to-r from-indigo-100 to-purple-100 border-2 border-indigo-300 rounded-2xl p-8">
                <h3 className="text-2xl font-bold mb-6 flex items-center gap-3">
                  <Award className="w-7 h-7 text-indigo-600" />
                  Comment Utiliser Ces Concepts
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Lis avec intention</h4>
                      <p className="text-sm text-gray-700">Ne survole pas. Prends 5 min par concept. Laisse infuser.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Choisis 1-2 concepts</h4>
                      <p className="text-sm text-gray-700">Pas 22 à la fois. Focus sur ce qui résonne MAINTENANT.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Réponds aux questions</h4>
                      <p className="text-sm text-gray-700">Les questions de réflexion sont le vrai travail. Écris les réponses.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Passe à l'action</h4>
                      <p className="text-sm text-gray-700">L'action concrète suggérée n'est pas optionnelle. DO IT.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Citation finale */}
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl p-8 text-center">
                <div className="text-5xl mb-4">🚀</div>
                <blockquote className="text-2xl font-bold italic mb-4">
                  "La croissance n'est pas un événement. C'est un processus quotidien de petites décisions courageuses."
                </blockquote>
                <p className="text-purple-100">— Philosophie BZ</p>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setOngletActif('concepts')}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:shadow-xl transition-all flex items-center gap-3 mx-auto"
                >
                  Découvrir les 22 Concepts
                  <Sparkles className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* ONGLET CONCEPTS */}
          {ongletActif === 'concepts' && (
            <div className="space-y-6">
              
              {/* Filtres */}
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => setCategorieActive('Toutes')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    categorieActive === 'Toutes'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Toutes ({philosophiesCroissance.length})
                </button>
                {(['Mindset', 'Action', 'Bonheur', 'Humilité', 'Croissance'] as const).map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setCategorieActive(cat)}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      categorieActive === cat
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {cat} ({statsPhilosophie.categories[cat]})
                  </button>
                ))}
              </div>

              {/* Liste des concepts */}
              {philosophieSelectionnee ? (
                // VUE DÉTAILLÉE D'UN CONCEPT
                <div className="space-y-6">
                  <button
                    onClick={() => setPhilosophieSelectionnee(null)}
                    className="text-purple-600 hover:text-purple-700 font-semibold flex items-center gap-2"
                  >
                    ← Retour à la liste
                  </button>

                  <div className={`bg-gradient-to-br ${philosophieSelectionnee.couleur} text-white rounded-2xl p-8`}>
                    <div className="flex items-start gap-4 mb-4">
                      <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4">
                        {iconeMap[philosophieSelectionnee.icone]}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold">
                            #{philosophieSelectionnee.numero}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-sm font-bold ${couleurCategorie[philosophieSelectionnee.categorie]} bg-white/90`}>
                            {philosophieSelectionnee.categorie}
                          </span>
                        </div>
                        <h3 className="text-3xl font-black mb-3">{philosophieSelectionnee.titre}</h3>
                        <p className="text-lg leading-relaxed opacity-95">
                          {philosophieSelectionnee.description}
                        </p>
                      </div>
                    </div>

                    {philosophieSelectionnee.origine && (
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl px-4 py-2 inline-block">
                        <p className="text-sm">
                          <strong>Origine:</strong> {philosophieSelectionnee.origine}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Histoire / Exemple */}
                  <div className="bg-white border-2 border-gray-200 rounded-2xl p-6">
                    <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <BookOpen className="w-6 h-6 text-blue-600" />
                      Histoire Concrète
                    </h4>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {philosophieSelectionnee.histoire}
                    </p>
                  </div>

                  {/* Questions de réflexion */}
                  <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-2xl p-6">
                    <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Brain className="w-6 h-6 text-orange-600" />
                      Questions de Réflexion (Le Vrai Travail)
                    </h4>
                    <ul className="space-y-3">
                      {philosophieSelectionnee.questionReflexion.map((q, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="bg-orange-500 text-white w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 font-bold text-sm">
                            {i + 1}
                          </div>
                          <p className="text-gray-700 font-medium">{q}</p>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Action concrète */}
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-2xl p-6">
                    <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                      <Zap className="w-6 h-6 text-green-600" />
                      Action Concrète (DO IT)
                    </h4>
                    <p className="text-gray-700 leading-relaxed font-medium text-lg">
                      {philosophieSelectionnee.actionConcrete}
                    </p>
                  </div>

                  {/* Citation */}
                  {philosophieSelectionnee.citation && (
                    <div className={`bg-gradient-to-r ${philosophieSelectionnee.couleur} text-white rounded-2xl p-6 text-center`}>
                      <div className="text-4xl mb-3">💡</div>
                      <blockquote className="text-xl font-bold italic">
                        "{philosophieSelectionnee.citation}"
                      </blockquote>
                    </div>
                  )}
                </div>
              ) : (
                // LISTE DES CONCEPTS
                <div className="grid grid-cols-2 gap-6">
                  {philosophiesFiltrees.map((philo) => (
                    <div
                      key={philo.id}
                      onClick={() => setPhilosophieSelectionnee(philo)}
                      className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:shadow-xl hover:border-purple-300 transition-all cursor-pointer group"
                    >
                      <div className="flex items-start gap-4 mb-4">
                        <div className={`bg-gradient-to-br ${philo.couleur} text-white rounded-xl p-3 group-hover:scale-110 transition-transform`}>
                          {iconeMap[philo.icone]}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded-full text-xs font-bold">
                              #{philo.numero}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-bold border ${couleurCategorie[philo.categorie]}`}>
                              {philo.categorie}
                            </span>
                          </div>
                          <h4 className="font-bold text-lg text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
                            {philo.titre}
                          </h4>
                          <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                            {philo.description}
                          </p>
                        </div>
                      </div>

                      {philo.origine && (
                        <div className="text-xs text-gray-500 italic mb-3">
                          {philo.origine}
                        </div>
                      )}

                      <div className="text-purple-600 font-semibold text-sm flex items-center gap-2 group-hover:gap-3 transition-all">
                        Lire la suite
                        <Sparkles className="w-4 h-4" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhilosophieCroissanceModal;





